import { RequestGuard } from '@msc-b2c/interfaces/types';

// Keeps request control local to each consumer: dedupe in-flight calls and skip
// re-fetch for the last successfully opened item to limit INT135 traffic.
export const createRequestGuard = <T>(): RequestGuard<T> => {
  let lastSuccessfulRequestKey: string | null = null;
  let lastSuccessfulResult: T | null = null;
  let inFlight: { key: string; promise: Promise<T> } | null = null;

  const run = (
    requestKey: string,
    request: () => Promise<T>,
    onSuccess: (result: T) => void,
    onError?: (error: unknown) => void
  ) => {
    // Same item opened again consecutively: reuse already loaded drawer data.
    if (lastSuccessfulRequestKey === requestKey && lastSuccessfulResult !== null) {
      onSuccess(lastSuccessfulResult);
      return;
    }

    // Prevent duplicate parallel calls while the same request is still running.
    if (inFlight?.key === requestKey) {
      inFlight.promise.then(onSuccess).catch((err) => {
        onError?.(err);
      });
      return;
    }

    const requestPromise = request();
    inFlight = { key: requestKey, promise: requestPromise };

    requestPromise
      .then((result) => {
        lastSuccessfulRequestKey = requestKey;
        lastSuccessfulResult = result;
        onSuccess(result);
      })
      .catch((err) => {
        onError?.(err);
        // Failed requests are never promoted to successful key, so next open retries.
      })
      .finally(() => {
        // Prevent duplicate parallel calls while the same request is still running.
        if (inFlight?.key === requestKey) {
          inFlight = null;
        }
      });
  };

  const reset = () => {
    lastSuccessfulRequestKey = null;
    lastSuccessfulResult = null;
    inFlight = null;
  };

  return { run, reset };
};
